/* Formula → DHA → size. All changes go through Prestige's native option radios;
 * its section rendering owns price, variant ID, gallery, sticky bar and cart.
 * Prices/availability come from current Liquid, never the metadata snapshot.
 */
(function () {
  'use strict';
  if (customElements.get('sobe-professional-selector')) return;

  const COPY = {
    en: { formula: 'Formula', strength: 'DHA strength', size: 'Size', caramel: 'Caramel', clear: 'Clear', brown: 'Brown', 'no-dha': 'Body Makeup · No DHA', liters: '1 liter', half: '500 ml', gallon: '1 gallon', pair: '1 liter comes in one aluminum bottle.', brownNote: 'Bienvenido a Miami — choose your DHA percentage below.', noDhaNote: 'Body Makeup contains no DHA.', upgrade: 'Choose gallon', error: 'The option could not be updated. Please try again.' },
    es: { formula: 'Fórmula', strength: 'Porcentaje de DHA', size: 'Tamaño', caramel: 'Caramel', clear: 'Transparente', brown: 'Marrón', 'no-dha': 'Maquillaje corporal · Sin DHA', liters: '1 litro', half: '500 ml', gallon: '1 galón', pair: '1 litro se presenta en una botella de aluminio.', brownNote: 'Bienvenido a Miami — elige el porcentaje de DHA abajo.', noDhaNote: 'El maquillaje corporal no contiene DHA.', upgrade: 'Elegir galón', error: 'No se pudo actualizar la opción. Inténtalo de nuevo.' }
  };
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  class ProfessionalSelector extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-sp-professional-config]').textContent);
        this.picker = this.closest('variant-picker');
        this.ui = this.querySelector('[data-sp-professional-ui]');
        const { facts, variants, variant_id } = this.config;
        this.rows = variants.map(variant => ({ ...facts.find(fact => fact.id === variant.id), variant }));
        // Do not conceal new/unmapped products, options or variants.
        if (!facts.length || variants.length !== facts.length || this.rows.some(row => !row.formula) || !this.picker || !this.ui) return;
        this.current = this.rows.find(row => row.id === variant_id);
        if (!this.current || this.rows.some(row => !this.optionValues(row.variant))) return;
        this.copy = COPY[this.config.locale.startsWith('es') ? 'es' : 'en'];
        this.controlIdPrefix = this.dataset.form + '-professional-';
        this.ready = true;
        this.render();
        this.ui.hidden = false;
        this.picker.classList.add('sp-picker--professional-ready');
        this.addEventListener('keydown', event => this.onKey(event));
      } catch (_) {
        // Native options remain visible if the enhancement cannot initialize.
      }
    }
    optionValues(variant) {
      const values = this.config.options.map(option => option.values.find(value => value.label === variant.options[option.position - 1])?.id);
      return values.every(Boolean) ? values : null;
    }
    find(formula, strength, liters) {
      return this.rows.find(row => row.formula === formula && row.strength === strength && row.liters === liters);
    }
    group(key, choices, selected, choose) {
      const field = element('fieldset', 'sp-prof-group');
      const legend = element('legend', '', this.copy[key]);
      legend.id = this.controlIdPrefix + key + '-legend';
      field.append(legend);
      const radios = element('div', 'sp-prof-options');
      radios.setAttribute('role', 'radiogroup');
      radios.setAttribute('aria-labelledby', legend.id);
      choices.forEach(choice => {
        const button = element('button', 'sp-prof-choice');
        button.type = 'button';
        button.id = this.controlIdPrefix + key + '-' + choice.key;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', String(choice.key === selected));
        button.tabIndex = choice.key === selected ? 0 : -1;
        button.disabled = !choice.row?.variant.available;
        button.dataset.unavailable = String(button.disabled);
        if (key === 'formula') {
          const dot = element('span', 'sp-prof-dot sp-prof-dot--' + choice.key);
          dot.setAttribute('aria-hidden', 'true');
          button.append(dot);
        }
        button.append(element('span', '', choice.label));
        button.addEventListener('click', () => choose(choice));
        radios.append(button);
      });
      field.append(radios);
      this.ui.append(field);
      return field;
    }
    render() {
      const cur = this.current;
      if (cur.formula !== 'single') {
        const formulas = [...new Set(this.rows.map(row => row.formula))];
        const choices = formulas.map(formula => {
          const strength = formula === 'no-dha' ? 0 : cur.strength || 10;
          return { key: formula, label: this.copy[formula], row: this.find(formula, strength, cur.liters) };
        });
        const field = this.group('formula', choices, cur.formula, choice => this.choose(choice.row));
        if (cur.formula === 'brown' || cur.formula === 'no-dha') field.append(element('p', 'sp-prof-note', this.copy[cur.formula === 'brown' ? 'brownNote' : 'noDhaNote']));
        if (cur.strength) {
          const strengths = [...new Set(this.rows.filter(row => row.formula === cur.formula).map(row => row.strength))].sort((a, b) => a - b);
          this.group('strength', strengths.map(strength => ({ key: strength, label: strength + '%', row: this.find(cur.formula, strength, cur.liters) })), cur.strength, choice => this.choose(choice.row));
        }
      }
      const field = this.group('size', [1, 0.5, 3.785411784].map(liters => ({ key: liters, label: this.copy[liters === 1 ? 'liters' : liters === 0.5 ? 'half' : 'gallon'], row: this.find(cur.formula, cur.strength, liters) })), cur.liters, choice => this.choose(choice.row));
      if (cur.liters === 1) field.append(element('p', 'sp-prof-note', this.copy.pair));
      this.savings();
    }
    savings() {
      const cur = this.current;
      const gallon = this.find(cur.formula, cur.strength, 3.785411784);
      const liter = this.find(cur.formula, cur.strength, 1);
      if (!gallon?.variant.available || !liter?.variant.available || liter.variant.price <= 0) return;
      const gallonPerLiter = gallon.variant.price / gallon.liters;
      if (gallonPerLiter >= liter.variant.price) return;
      const money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      const spanish = this.config.locale.startsWith('es');
      const box = element('div', 'sp-prof-savings');
      const headline = element('div', 'sp-prof-savings__headline');
      headline.append(element('p', 'sp-prof-savings__kicker', spanish ? 'MÁS PRODUCTO. MEJOR PRECIO.' : 'BIGGER SIZE. BETTER VALUE.'));
      headline.append(element('p', 'sp-prof-savings__intro', spanish ? 'Elige un galón.' : 'Go gallon.'));
      headline.append(element('strong', 'sp-prof-savings__amount', spanish ? 'Ahorra más.' : 'Save more.'));
      box.append(headline);
      if (gallon.variant.featured_image) {
        const image = element('img', 'sp-prof-savings__image');
        image.src = gallon.variant.featured_image.src;
        image.alt = gallon.variant.featured_media?.alt || '';
        image.width = 104;
        image.height = 104;
        image.loading = 'lazy';
        box.append(image);
      }
      box.append(element('p', '', spanish ? 'Un precio más bajo por litro que la opción de 1 litro.' : 'A lower price per liter than the 1-liter option.'));
      if (this.config.selling_plan_group_count > 0) {
        box.append(element('p', 'sp-prof-savings__subscription', spanish ? 'Elige «Suscríbete y ahorra» abajo para ahorrar aún más.' : 'Subscribe & save below for extra savings.'));
      }
      if (cur.liters !== gallon.liters) {
        const button = element('button', 'sp-prof-gallon', this.copy.upgrade + ' · ' + money.format(gallon.variant.price / 100) + ' →');
        button.type = 'button';
        button.id = this.controlIdPrefix + 'upgrade';
        button.addEventListener('click', () => this.choose(gallon));
        box.append(button);
      } else {
        box.append(element('p', 'sp-prof-savings__selected', spanish ? '✓ Galón seleccionado' : '✓ Gallon selected'));
      }
      this.ui.append(box);
    }
    async choose(row) {
      if (this.busy || !row?.variant.available || row.id === this.current.id) return;
      const values = this.optionValues(row.variant);
      if (!values) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const upgradeFocused = document.activeElement?.id === this.controlIdPrefix + 'upgrade';
      this.querySelectorAll('button:not(:disabled)').forEach(button => { button.setAttribute('aria-disabled', 'true'); });
      this.querySelector('.sp-prof-error')?.remove();
      try {
        await customElements.whenDefined('variant-picker');
        const form = document.forms[this.dataset.form];
        const inputs = [...form.elements].filter(input => input.matches('input[data-option-position]'));
        const changed = values.map(value => inputs.find(input => input.value === String(value))).filter(input => input && !input.checked);
        if (changed.length !== 1) throw new Error('No native option for this selection');
        await new Promise((resolve, reject) => {
          const done = event => {
            if (event.detail.variant?.id !== row.id) return;
            clearTimeout(timeout);
            form.removeEventListener('variant:change', done);
            resolve();
          };
          const timeout = setTimeout(() => {
            form.removeEventListener('variant:change', done);
            reject(new Error('Variant update timed out'));
          }, 15000);
          form.addEventListener('variant:change', done);
          // A real native option change also reaches apps that listen to inputs.
          changed[0].click();
        });
        // The replacement custom element renders the new selected variant.
        if (upgradeFocused) document.getElementById(this.controlIdPrefix + 'size-3.785411784')?.focus();
      } catch (_) {
        if (this.isConnected) {
          const error = element('p', 'sp-prof-error', this.copy.error);
          error.setAttribute('role', 'alert');
          this.ui.append(error);
        }
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (this.isConnected) this.querySelectorAll('button').forEach(button => { button.removeAttribute('aria-disabled'); });
      }
    }
    onKey(event) {
      const button = event.target.closest('[role="radio"]');
      if (!button || this.busy || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const buttons = [...button.parentElement.querySelectorAll('button:not(:disabled)')];
      const index = buttons.indexOf(button);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
      buttons[next]?.click();
    }
  }
  customElements.define('sobe-professional-selector', ProfessionalSelector);
})();
